import {
  getMenuTagDefinition,
  hydrateMenuTagRefs,
  resolveMenuItemLegalMarks,
} from '@web-studio/shared-schemas';
import { LucideIcon } from '../../icons/LucideIcon';
import { resolveLucideByIconKey } from '../../icons/menuTagLucideMap';
import type { MenuItem } from '../../lib/parseMenuColumns';
import {
  buildMenuItemLegendTipGroups,
  formatMenuItemLegendTipPlain,
} from '../../lib/menuItemLegendTip';

/**
 * Allergen letters + additive numbers as superscript after the dish name
 * (classic DE Speisekarte: „Burrataᴬ,ᶜ,¹“).
 * Full labels live on the dish-name legend tip (not a second native title).
 */
export function MenuItemLegalSuperscript({
  item,
  locale,
}: {
  item: MenuItem;
  locale: string;
}) {
  const marks = resolveMenuItemLegalMarks(item.allergenIds, item.additiveIds, locale);
  if (marks.length === 0) {
    return null;
  }
  const title = marks.map((mark) => `${mark.code} · ${mark.label}`).join(' · ');
  const text = marks.map((mark) => mark.code).join(',');
  return (
    <sup className="ws-menu__legal-sup" aria-label={title}>
      {text}
    </sup>
  );
}

function MenuItemLegendTip({
  groups,
}: {
  groups: ReturnType<typeof buildMenuItemLegendTipGroups>;
}) {
  if (groups.length === 0) {
    return null;
  }
  return (
    <span className="ws-menu__item-legend-tip" role="tooltip">
      {groups.map((group) => (
        <span key={group.title} className="ws-menu__item-legend-tip__group">
          <span className="ws-menu__item-legend-tip__title">{group.title}</span>
          {group.rows.map((row) => (
            <span key={row} className="ws-menu__item-legend-tip__row">
              {row}
            </span>
          ))}
        </span>
      ))}
    </span>
  );
}

/** Order-code column — keep outside the dish name so wraps stay in the copy column. */
export function MenuItemOrderCode({
  code,
  className = 'ws-menu__item-code',
}: {
  code?: string | null;
  className?: string;
}) {
  const orderCode = code?.trim() ?? '';
  return (
    <span className={className} aria-hidden={!orderCode}>
      {orderCode}
    </span>
  );
}

/** Price column slot — empty when missing so grid columns stay aligned. */
export function MenuItemPriceSlot({
  price,
  className = 'ws-menu__item-price',
}: {
  price?: string | null;
  className?: string;
}) {
  const value = price?.trim() ?? '';
  return (
    <p className={className} aria-hidden={!value}>
      {value}
    </p>
  );
}

/** Dish name with optional order code + legal superscripts. */
export function MenuItemName({
  item,
  locale,
  className = 'ws-menu__item-name',
  codeClassName = 'ws-menu__item-code',
  showLegalMarks = true,
  showOrderCode = true,
}: {
  item: MenuItem;
  locale: string;
  className?: string;
  codeClassName?: string;
  /** Full Speisekarte shows allergen/additive superscripts; highlight cards usually omit them. */
  showLegalMarks?: boolean;
  /**
   * Prefer `false` + sibling `MenuItemOrderCode` for list layouts so long names
   * and descriptions do not wrap under the code.
   */
  showOrderCode?: boolean;
}) {
  const legendGroups = buildMenuItemLegendTipGroups(item, locale, {
    includeLegalMarks: showLegalMarks,
  });
  const legendPlain = formatMenuItemLegendTipPlain(legendGroups);
  const hasLegendTip = legendGroups.length > 0;

  return (
    <p
      className={[className, hasLegendTip ? 'ws-menu__item-name--legend-tip' : '']
        .filter(Boolean)
        .join(' ')}
      tabIndex={hasLegendTip ? 0 : undefined}
      aria-label={hasLegendTip ? `${item.name}. ${legendPlain.replace(/\n/g, '. ')}` : undefined}
    >
      {showOrderCode && item.code?.trim() ? (
        <span className={codeClassName}>{item.code.trim()}</span>
      ) : null}
      {item.name}
      {showLegalMarks ? <MenuItemLegalSuperscript item={item} locale={locale} /> : null}
      <MenuItemLegendTip groups={legendGroups} />
    </p>
  );
}

/**
 * Dietary claims as chips — icon when available, label by default.
 * Pass `iconsOnly` for compact / highlight layouts; `placement="inline"` sits
 * beside the dish name (Category Chips Speisekarte).
 */
export function MenuItemDietaryTags({
  item,
  locale,
  iconsOnly = false,
  placement = 'stack',
}: {
  item: MenuItem;
  locale: string;
  /** Icon glyph only (skip tags without an icon); tooltip via data-label. */
  iconsOnly?: boolean;
  /** `inline` = after dish name in the head row; `stack` = below description. */
  placement?: 'stack' | 'inline';
}) {
  const dietary = hydrateMenuTagRefs(item.dietaryTagIds, locale).filter((tag) => {
    const def = getMenuTagDefinition(tag.id);
    if (def && def.kind !== 'dietary') {
      return false;
    }
    if (iconsOnly && !resolveLucideByIconKey(tag.iconKey)) {
      return false;
    }
    return true;
  });
  if (dietary.length === 0) {
    return null;
  }
  const inline = placement === 'inline';
  return (
    <div
      className={[
        'ws-menu__dietary',
        iconsOnly ? 'ws-menu__dietary--icons' : '',
        inline ? 'ws-menu__dietary--inline' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role="list"
    >
      {dietary.map((tag) => {
        const Icon = resolveLucideByIconKey(tag.iconKey);
        return (
          <span
            key={tag.id}
            className={[
              'ws-menu__dietary-chip',
              iconsOnly ? 'ws-menu__dietary-chip--tip' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            role="listitem"
            data-label={tag.label}
            aria-label={tag.label}
            title={iconsOnly ? undefined : tag.label}
            tabIndex={iconsOnly ? 0 : undefined}
            onPointerDown={
              iconsOnly
                ? (event) => {
                    event.stopPropagation();
                  }
                : undefined
            }
            onPointerUp={
              iconsOnly
                ? (event) => {
                    event.stopPropagation();
                  }
                : undefined
            }
            onClick={
              iconsOnly
                ? (event) => {
                    event.stopPropagation();
                  }
                : undefined
            }
          >
            {Icon ? (
              <LucideIcon
                icon={Icon}
                size={iconsOnly ? (inline ? 'sm' : 'md') : 'sm'}
                strokeWidth={iconsOnly ? 2.25 : 1.75}
                className="ws-menu__dietary-icon"
              />
            ) : null}
            {iconsOnly ? null : <span className="ws-menu__dietary-label">{tag.label}</span>}
          </span>
        );
      })}
    </div>
  );
}
